import React, { useState, useEffect } from "react";
import {
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  Grid,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  Alert,
  Switch,
  FormControlLabel,
  Fab,
  Tooltip,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  ListItemAvatar,
  Slider,
  Tab,
  Tabs,
  ListItemSecondaryAction,
  Snackbar,
  Badge,
} from "@mui/material";
import {
  Settings as SettingsIcon,
  AutoMode as AutoModeIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  LocationOn as LocationIcon,
  Route as RouteIcon,
  Schedule as ScheduleIcon,
  DirectionsCar as CarIcon,
  Phone as PhoneIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Map as MapIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  Reorder as ReorderIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Tune as TuneIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  Speed as SpeedIcon,
  Route as OptimizedIcon,
} from "@mui/icons-material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useNavigate } from "react-router-dom";
import RouteMap from "../../components/RouteMap";
import { routeService } from "../../services/routeService";
import { orderService } from "../../services/orderService";
import { userService } from "../../services/userService";
import { apiService } from "../../services/api";

const RouteOptimization = () => {
  // State management
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [reoptimizing, setReoptimizing] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState("");
  const [numGroups, setNumGroups] = useState(3);
  const [selectedMedicalProfessional, setSelectedMedicalProfessional] = useState("");
  const [scheduledDate, setScheduledDate] = useState("");
  const [routeSuggestions, setRouteSuggestions] = useState([]);
  const [createdRoutes, setCreatedRoutes] = useState([]);
  const [existingRoutes, setExistingRoutes] = useState([]);
  const [unassignedOrders, setUnassignedOrders] = useState([]);
  const [medicalProfessionals, setMedicalProfessionals] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [autoMode, setAutoMode] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [reorderDialogOpen, setReorderDialogOpen] = useState(false);
  const [selectedRouteForReorder, setSelectedRouteForReorder] = useState(null);

  // Enhanced optimization settings
  const [optimizationSettings, setOptimizationSettings] = useState({
    maxStopsPerRoute: 8,
    maxHoursPerRoute: 8,
    bufferTimeMinutes: 15,
    lunchBreakMinutes: 30,
    allowRushHour: false,
    prioritizeUrgent: true,
    useGoogleMapsOptimization: true,
    considerTrafficPatterns: true,
    defaultStartingPoint: "", // Start empty, will be set from MP data
  });

  useEffect(() => {
    // Set tomorrow as default date
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setScheduledDate(tomorrow.toISOString().split("T")[0]);

    // Load facilities and existing config
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedFacility && scheduledDate) {
      loadExistingRoutes();
      loadUnassignedOrders();
    }
  }, [selectedFacility, scheduledDate]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // FIXED: Load actual facilities from API instead of hardcoded
      const facilitiesResponse = await apiService.get("/facilities");

      if (facilitiesResponse.data && facilitiesResponse.data.length > 0) {
        setFacilities(facilitiesResponse.data);
        // Set the first facility as default or user's facility if available
        const userFacility = facilitiesResponse.data.find(
          (f) => f.is_current_user_facility
        );
        setSelectedFacility(userFacility?.id || facilitiesResponse.data[0].id);
      } else {
        // Fallback for facility admin users - get from user context
        try {
          const userResponse = await apiService.get("/auth/me");
          const currentUser = userResponse.data;

          if (currentUser.facility_id) {
            setFacilities([
              {
                id: currentUser.facility_id,
                name: currentUser.facility_name || "My Facility",
                company_name: currentUser.facility_name || "My Facility",
              },
            ]);
            setSelectedFacility(currentUser.facility_id);
          } else {
            // Ultimate fallback
            setFacilities([
              {
                id: 1,
                name: "Default Facility",
                company_name: "Default Facility",
              },
            ]);
            setSelectedFacility(1);
          }
        } catch (userError) {
          console.warn("Could not get user context, using fallback");
          setFacilities([
            {
              id: 1,
              name: "Default Facility",
              company_name: "Default Facility",
            },
          ]);
          setSelectedFacility(1);
        }
      }

      // Load medical professionals with starting points FIRST
      await loadMedicalProfessionals();

      // Load existing route configuration but don't override the MP starting point
      try {
        const config = await routeService.getFacilityConfig(selectedFacility);
        if (config) {
          setOptimizationSettings((prev) => ({
            ...prev,
            maxStopsPerRoute: config.max_orders_per_route || 8,
            // Don't override defaultStartingPoint here - it should come from MP data
          }));
        }
      } catch (err) {
        console.warn("No existing config found, using defaults");
      }
    } catch (err) {
      console.error("Error loading initial data:", err);
      setError("Error loading initial data");
      // Set fallback data
      setFacilities([
        { id: 1, name: "Default Facility", company_name: "Default Facility" },
      ]);
      setSelectedFacility(1);
    } finally {
      setLoading(false);
    }
  };

  const loadMedicalProfessionals = async () => {
    try {
      // ðŸ"¥ NEW: Use the enhanced endpoint that includes starting points
      const response =
        await routeService.getMedicalProfessionalsWithStartingPoints();
      setMedicalProfessionals(response || []);

      // FIXED: Update default starting point immediately after loading MPs
      const mpWithStartingPoint = (response || []).find(
        (mp) => mp.has_starting_point && mp.starting_location
      );

      if (mpWithStartingPoint && mpWithStartingPoint.starting_location) {
        const startingPoint = mpWithStartingPoint.starting_location;
        let address = "Main Medical Center, Los Angeles, CA"; // fallback

        // Use the address if available, otherwise construct from name
        if (startingPoint.address) {
          address = startingPoint.address;
        } else if (startingPoint.name) {
          address = startingPoint.name;
        } else if (startingPoint.lat && startingPoint.lng) {
          address = `${startingPoint.lat}, ${startingPoint.lng}`;
        }


        setOptimizationSettings((prev) => ({
          ...prev,
          defaultStartingPoint: address,
        }));
      } else {
        // No MP has starting point configured, use Main Medical Center
        setOptimizationSettings((prev) => ({
          ...prev,
          defaultStartingPoint: "Main Medical Center, Los Angeles, CA",
        }));
      }
    } catch (err) {
      console.error("Error loading medical professionals:", err);
      // Fallback to regular MP endpoint
      try {
        const fallbackResponse = await userService.getMedicalProfessionals();
        setMedicalProfessionals(fallbackResponse || []);
        // Set fallback since we don't have starting point data
        setOptimizationSettings((prev) => ({
          ...prev,
          defaultStartingPoint: "Main Medical Center, Los Angeles, CA",
        }));
      } catch (fallbackErr) {
        console.error("Fallback MP loading also failed:", fallbackErr);
        setMedicalProfessionals([]);
        setOptimizationSettings((prev) => ({
          ...prev,
          defaultStartingPoint: "Main Medical Center, Los Angeles, CA",
        }));
      }
    }
  };

  // REMOVED: updateDefaultStartingPointFromMPs function - logic moved directly into loadMedicalProfessionals

  const loadExistingRoutes = async () => {
    if (!selectedFacility || !scheduledDate) return;

    try {
      const routes = await routeService.getDailyRoutes(
        selectedFacility,
        scheduledDate
      );

      // Enhance routes with order details and metrics
      const enhancedRoutes = await Promise.all(
        routes.map(async (route) => {
          try {
            const orders = await routeService.getRouteOrders(route.id);
            const mp = medicalProfessionals.find(
              (mp) => mp.id === route.medical_professional_id
            );

            return {
              ...route,
              orders: orders || [],
              efficiency: calculateRouteEfficiency(orders || []),
              totalDistance: calculateTotalDistance(orders || []),
              estimatedDuration: calculateEstimatedDuration(orders || []),
              // ðŸ"¥ NEW: Include MP starting point info
              medical_professional_info: mp
                ? {
                    name: mp.user?.full_name || `MP-${mp.id}`,
                    starting_location: mp.starting_location,
                    has_starting_point: mp.has_starting_point,
                  }
                : null,
            };
          } catch (err) {
            console.error(`Error loading orders for route ${route.id}:`, err);
            return {
              ...route,
              orders: [],
              efficiency: 0,
              totalDistance: 0,
              estimatedDuration: 0,
              medical_professional_info: null,
            };
          }
        })
      );

      setExistingRoutes(enhancedRoutes);
    } catch (err) {
      console.error("Error loading existing routes:", err);
      setExistingRoutes([]);
    }
  };

  const loadUnassignedOrders = async () => {
    if (!selectedFacility || !scheduledDate) return;

    try {
      const response = await orderService.getAllOrders({
        facility_id: selectedFacility,
        scheduled_date: scheduledDate,
        status: "assigned", // Orders assigned to MPs but not in routes
      });

      // Filter orders that are assigned to MPs but not in routes
      const unassigned = response.filter(
        (order) => order.medical_professional_id && !order.route_group_id
      );

      setUnassignedOrders(unassigned);
    } catch (err) {
      console.error("Error loading unassigned orders:", err);
      setUnassignedOrders([]);
    }
  };

  const calculateRouteEfficiency = (orders) => {
    if (orders.length === 0) return 0;

    // Simple efficiency calculation: service time vs total time
    const totalServiceTime = orders.reduce((sum, order) => sum + 30, 0); // 30 min per order
    const totalTravelTime = orders.length * 20; // 20 min average travel
    const totalTime = totalServiceTime + totalTravelTime;

    return Math.round((totalServiceTime / totalTime) * 100);
  };

  const calculateTotalDistance = (orders) => {
    // Rough estimate: 8 miles average per stop
    return (orders.length * 8.5).toFixed(1);
  };

  const calculateEstimatedDuration = (orders) => {
    // 30 min service + 20 min travel per order, plus buffer
    const baseTime = orders.length * 50; // minutes
    const bufferTime = orders.length * optimizationSettings.bufferTimeMinutes;
    return Math.round(((baseTime + bufferTime) / 60) * 10) / 10; // hours
  };

  // ðŸ"¥ FIXED: Use the enhanced optimization endpoint
  const handleOptimize = async () => {
    if (!selectedFacility) {
      setError("Please select a facility");
      return;
    }

    if (!scheduledDate) {
      setError("Please select a scheduled date");
      return;
    }

    setOptimizing(true);
    setError("");

    try {

      // Auto-calculate numGroups from assigned technicians/orders
      const autoNumGroups = selectedMedicalProfessional
        ? 1
        : Math.max(1, medicalProfessionals.length || 3);

      const optimizeRequest = {
        scheduled_date: scheduledDate,
        medical_professional_id: selectedMedicalProfessional || null,
        config: {
          max_routes_per_day: autoNumGroups,
          max_orders_per_route: optimizationSettings.maxStopsPerRoute,
          buffer_time_minutes: optimizationSettings.bufferTimeMinutes,
          prioritize_urgent: optimizationSettings.prioritizeUrgent,
          default_starting_point: optimizationSettings.defaultStartingPoint,
          use_google_maps_optimization:
            optimizationSettings.useGoogleMapsOptimization,
        },
      };


      // Use the enhanced optimize endpoint that handles MP starting points
      const response = await routeService.optimizeRoutes(optimizeRequest);

      setRouteSuggestions(response || []);

      if (!response || response.length === 0) {
        setError(
          "No route suggestions generated. Please ensure orders are assigned to medical professionals and have valid addresses."
        );
      } else {
        setSuccess(
          `Generated ${response.length} optimized routes with medical professional starting points!`
        );
      }
    } catch (err) {
      console.error("Error optimizing routes:", err);
      setError(
        err.response?.data?.detail ||
          err.message ||
          "Error generating route optimization"
      );
    } finally {
      setOptimizing(false);
    }
  };

  // ðŸ"¥ FIXED: Use the enhanced create routes endpoint
  const handleCreateRoutes = async () => {
    if (!selectedFacility || !scheduledDate || routeSuggestions.length === 0) {
      setError("Please optimize routes first");
      return;
    }

    setCreating(true);
    setError("");

    try {

      const response = await routeService.createRouteGroups({
        routes: routeSuggestions,
        scheduled_date: scheduledDate,
      });

      setCreatedRoutes(response || []);
      setSuccess("Optimized routes created successfully!");

      // Refresh existing routes and clear suggestions
      setRouteSuggestions([]);
      await loadExistingRoutes();
      await loadUnassignedOrders();
    } catch (err) {
      console.error("Error creating routes:", err);
      setError(err.response?.data?.detail || "Error creating route groups");
    } finally {
      setCreating(false);
    }
  };

  // ðŸ"¥ FIXED: Enhanced reoptimization using new endpoints
  const handleReoptimizeRoutes = async () => {
    if (!selectedFacility || !scheduledDate) {
      setError("Please select facility and date");
      return;
    }

    setReoptimizing(true);
    setError("");

    try {

      // Use the generate endpoint which includes optimization
      const autoNumGroups = selectedMedicalProfessional
        ? 1
        : Math.max(1, medicalProfessionals.length || 3);

      const generateRequest = {
        scheduledDate: scheduledDate,
        maxRoutes: autoNumGroups,
        medical_professional_id: selectedMedicalProfessional || null,
      };

      const suggestions = await routeService.generateRoutes(generateRequest);

      if (suggestions?.routes && suggestions.routes.length > 0) {
        // Create new routes from suggestions
        const response = await routeService.createRouteGroups({
          routes: suggestions.routes,
          scheduled_date: scheduledDate,
        });

        setCreatedRoutes(response || []);
        setSuccess(
          `Routes reoptimized successfully! Generated ${suggestions.routes.length} optimized routes.`
        );

        // Clear suggestions and reload
        setRouteSuggestions([]);
        await loadExistingRoutes();
        await loadUnassignedOrders();
      } else {
        setError("No optimization suggestions generated");
      }
    } catch (err) {
      console.error("Error reoptimizing routes:", err);
      setError(err.response?.data?.detail || "Error reoptimizing routes");
    } finally {
      setReoptimizing(false);
    }
  };

  const getRouteStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "success";
      case "in_progress":
        return "primary";
      case "assigned":
        return "info";
      case "created":
        return "warning";
      default:
        return "default";
    }
  };

  const estimateRouteDuration = (orders) => {
    // Rough estimate: 30 minutes per order + travel time
    const baseTime = orders.length * 30;
    const travelTime = Math.max(orders.length - 1, 0) * 15; // 15 min between stops
    return baseTime + travelTime;
  };

  // ðŸ"¥ ENHANCED: Better maps integration with starting points
  const openInGoogleMaps = (route) => {
    if (!route.orders || route.orders.length === 0) {
      setError("No orders in this route to display in maps");
      return;
    }

    // ðŸ"¥ NEW: Use medical professional starting point if available
    let startPoint = optimizationSettings.defaultStartingPoint;

    if (route.starting_point) {
      if (route.starting_point.lat && route.starting_point.lng) {
        startPoint = `${route.starting_point.lat},${route.starting_point.lng}`;
      } else if (route.starting_point.name || route.starting_point.address) {
        startPoint = route.starting_point.name || route.starting_point.address;
      }
    }


    const validAddresses = route.orders
      .map((order) => order.patient_address || order.address)
      .filter(Boolean);

    if (validAddresses.length === 0) {
      setError("No valid addresses found in route orders");
      return;
    }

    const origin = encodeURIComponent(startPoint);

    if (validAddresses.length === 1) {
      const destination = encodeURIComponent(validAddresses[0]);
      const url = `https://www.google.com/maps/dir/${origin}/${destination}`;
      window.open(url, "_blank");
    } else {
      const destinations = validAddresses
        .map((addr) => encodeURIComponent(addr))
        .join("/");

      const url = `https://www.google.com/maps/dir/${origin}/${destinations}`;
      window.open(url, "_blank");
    }
  };

  const handleReorderRoute = (route) => {
    setSelectedRouteForReorder(route);
    setReorderDialogOpen(true);
  };

  const handleReorderSave = async (newOrderSequence) => {
    if (!selectedRouteForReorder) return;

    try {
      const orderIds = newOrderSequence.map((order) => order.id);
      await routeService.reorderRouteOrders(selectedRouteForReorder.id, {
        order_ids: orderIds,
      });

      setSuccess("Route order updated successfully");
      setReorderDialogOpen(false);
      setSelectedRouteForReorder(null);

      // Reload routes
      await loadExistingRoutes();
    } catch (err) {
      setError(`Failed to reorder route: ${err.message}`);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case "urgent":
        return "error";
      case "same_day":
        return "warning";
      case "routine":
        return "info";
      default:
        return "default";
    }
  };

  const getEfficiencyColor = (efficiency) => {
    if (efficiency >= 80) return "success";
    if (efficiency >= 60) return "warning";
    return "error";
  };

  // ðŸ"¥ NEW: Helper to get optimization status
  const getOptimizationStatus = (route) => {
    if (route.optimized || route.starting_point) {
      return {
        color: "success",
        label: "Optimized",
        icon: <OptimizedIcon />,
      };
    }
    return {
      color: "warning",
      label: "Basic",
      icon: <WarningIcon />,
    };
  };

  const TabPanel = ({ children, value, index }) => (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );

  // FIXED: Add error boundary and better loading states
  if (loading) {
    return (
      <Box>
        <LinearProgress />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <Typography>Loading route optimization...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Success/Error Messages */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setError("")} severity="error">
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setSuccess("")} severity="success">
          {success}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4">Smart Route Optimization</Typography>
        <Box>
          <Tooltip title="Optimization Settings">
            <Fab
              color="primary"
              size="small"
              onClick={() => setSettingsOpen(true)}
              sx={{ mr: 2 }}
            >
              <TuneIcon />
            </Fab>
          </Tooltip>
          <FormControlLabel
            control={
              <Switch
                checked={autoMode}
                onChange={(e) => setAutoMode(e.target.checked)}
                color="primary"
              />
            }
            label="Auto Mode"
          />
        </Box>
      </Box>

      {/* ðŸ"¥ ENHANCED: Medical Professional Starting Points Status */}
      <Paper
        elevation={0}
        sx={{ p: 3, mb: 3, border: "1px solid #E2E8F0", borderRadius: 3, bgcolor: "#F0F9FF" }}
      >
        <Typography variant="h6" gutterBottom>
          Medical Professional Starting Points
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
          Configure individual starting points for each medical professional for
          optimal route optimization. Routes will begin from these points
          instead of the default facility location.
        </Typography>

        <Grid container spacing={2}>
          {medicalProfessionals.map((mp) => (
            <Grid key={mp.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <Card variant="outlined" sx={{ bgcolor: "background.paper" }}>
                <CardContent sx={{ py: 2 }}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Avatar
                      src={mp.user?.avatar_url}
                      sx={{
                        width: 32,
                        height: 32,
                        mr: 1,
                        bgcolor: "primary.main",
                      }}
                    >
                      {mp.user?.full_name?.charAt(0) || "MP"}
                    </Avatar>
                    <Typography variant="subtitle2" color="text.primary">
                      {mp.user?.full_name || `MP-${mp.id}`}
                    </Typography>
                  </Box>

                  <Box display="flex" alignItems="center" gap={1}>
                    {mp.has_starting_point ? (
                      <>
                        <Chip
                          icon={
                            mp.starting_location?.type === "home" ? (
                              <HomeIcon />
                            ) : (
                              <BusinessIcon />
                            )
                          }
                          label="Configured"
                          color="success"
                          size="small"
                        />
                        <Tooltip
                          title={
                            mp.starting_location?.address ||
                            "Address not available"
                          }
                        >
                          <LocationIcon color="success" />
                        </Tooltip>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            navigate(`/medical-professionals/${mp.id}/edit`)
                          }
                        >
                          Edit
                        </Button>
                      </>
                    ) : (
                      <>
                        <Chip
                          icon={<WarningIcon />}
                          label="Not Set"
                          color="warning"
                          size="small"
                        />
                        <Button
                          size="small"
                          variant="contained"
                          color="warning"
                          onClick={() =>
                            navigate(`/medical-professionals/${mp.id}/edit`)
                          }
                        >
                          Configure
                        </Button>
                      </>
                    )}
                  </Box>

                  {mp.starting_location && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 0.5 }}
                    >
                      {" "}
                      {mp.starting_location.name ||
                        mp.starting_location.address}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {medicalProfessionals.filter((mp) => !mp.has_starting_point).length >
          0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Optimization Notice
            </Typography>
            <Typography variant="body2">
              {
                medicalProfessionals.filter((mp) => !mp.has_starting_point)
                  .length
              }{" "}
              medical professionals don't have starting points configured.
              Routes for these professionals will use the default starting point
              below, which may not be optimal.
            </Typography>
          </Alert>
        )}
      </Paper>

      {/* FIXED: Add unassigned orders info */}
      {unassignedOrders.length > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Ready for Optimization
          </Typography>
          <Typography variant="body2">
            Found {unassignedOrders.length} orders assigned to medical
            professionals but not yet in routes.
            {
              medicalProfessionals.filter((mp) => mp.has_starting_point).length
            }{" "}
            of {medicalProfessionals.length}
            medical professionals have configured starting points for enhanced
            optimization.
          </Typography>
        </Alert>
      )}

      {/* Existing Routes Section */}
      {existingRoutes.length > 0 && (
        <Paper elevation={0} sx={{ p: 3, mb: 3, border: "1px solid #E2E8F0", borderRadius: 3 }}>
          <Typography variant="h6" gutterBottom>
            Existing Routes for{" "}
            {new Date(scheduledDate + "T00:00:00").toLocaleDateString()}
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            {existingRoutes.map((route) => {
              const optimizationStatus = getOptimizationStatus(route);
              return (
                <Grid key={route.id} size={{ xs: 12, md: 6, lg: 4 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1}
                      >
                        <Typography variant="h6">{route.name}</Typography>
                        <Chip
                          label={route.status}
                          color={getRouteStatusColor(route.status)}
                          size="small"
                        />
                      </Box>

                      {/* ðŸ"¥ NEW: Show MP info and starting point status */}
                      {route.medical_professional_info && (
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <PersonIcon color="primary" />
                          <Typography variant="body2" color="text.secondary">
                            {route.medical_professional_info.name}
                          </Typography>
                          {route.medical_professional_info
                            .has_starting_point && (
                            <Chip
                              icon={<LocationIcon />}
                              label="Starting Point"
                              color="success"
                              size="small"
                            />
                          )}
                        </Box>
                      )}

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        {route.orders.length} orders • {route.estimatedDuration}
                        h • {route.totalDistance} mi
                      </Typography>

                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <Chip
                          label={`${route.efficiency}% efficient`}
                          color={getEfficiencyColor(route.efficiency)}
                          size="small"
                        />
                        <Chip
                          icon={optimizationStatus.icon}
                          label={optimizationStatus.label}
                          color={optimizationStatus.color}
                          size="small"
                        />
                      </Box>

                      <Box display="flex" gap={1} flexWrap="wrap">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => navigate(`/routes/${route.id}`)}
                        >
                          Details
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<MapIcon />}
                          onClick={() => openInGoogleMaps(route)}
                        >
                          Maps
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<ReorderIcon />}
                          onClick={() => handleReorderRoute(route)}
                        >
                          Reorder
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Route Map Overview */}
          <Box mt={3} mb={3}>
            <Typography variant="subtitle2" gutterBottom>
              Route Map Overview
            </Typography>
            <RouteMap
              routes={existingRoutes.map((route) => ({
                name: route.name,
                orders: route.orders || [],
                starting_point: route.medical_professional_info?.starting_location
                  ? {
                      lat: route.medical_professional_info.starting_location.latitude,
                      lng: route.medical_professional_info.starting_location.longitude,
                      name: route.medical_professional_info.starting_location.address || "Start",
                    }
                  : null,
                medical_professional_name: route.medical_professional_info?.name || route.name,
              }))}
              height="350px"
            />
          </Box>

          <Box mt={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleReoptimizeRoutes}
              disabled={reoptimizing}
              startIcon={<RefreshIcon />}
            >
              {reoptimizing ? "Reoptimizing..." : "Reoptimize All Routes"}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Optimization Settings */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: "1px solid #E2E8F0", borderRadius: 3 }}>
        <Typography variant="h6" gutterBottom>
          Smart Route Generation
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={3}>
          <Grid  size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth>
              <InputLabel>Facility</InputLabel>
              <Select
                value={selectedFacility}
                onChange={(e) => setSelectedFacility(e.target.value)}
                label="Facility"
                disabled={optimizing || creating}
              >
                {facilities.map((facility) => (
                  <MenuItem key={facility.id} value={facility.id}>
                    {facility.company_name || facility.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid  size={{ xs: 12, md: 4 }}>
            <TextField
              label="Scheduled Date"
              type="date"
              fullWidth
              value={scheduledDate}
              onChange={(e) => {
                setScheduledDate(e.target.value);
                setExistingRoutes([]);
                setUnassignedOrders([]);
              }}
              InputLabelProps={{ shrink: true }}
              disabled={optimizing || creating}
            />
          </Grid>

          <Grid  size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth>
              <InputLabel>Technician (optional)</InputLabel>
              <Select
                value={selectedMedicalProfessional || ""}
                onChange={(e) => setSelectedMedicalProfessional(e.target.value)}
                label="Technician (optional)"
                disabled={optimizing || creating}
              >
                <MenuItem value="">
                  <em>All Technicians</em>
                </MenuItem>
                {medicalProfessionals.map((mp) => (
                  <MenuItem key={mp.id} value={mp.id}>
                    {mp.user?.full_name || mp.name || `MP #${mp.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOptimize}
            disabled={optimizing || creating}
            startIcon={<AutoModeIcon />}
            sx={{ mr: 2 }}
          >
            {optimizing ? "🔍 Analyzing..." : "🚀 Generate Smart Routes"}
          </Button>

          <Button
            variant="outlined"
            onClick={() => setSettingsOpen(true)}
            startIcon={<SettingsIcon />}
          >
            Advanced Settings
          </Button>
        </Box>

        {optimizing && (
          <Box sx={{ mt: 3 }}>
            <LinearProgress />
            <Typography variant="body2" align="center" sx={{ mt: 1 }}>
              🔍 Grouping orders by medical professional and optimizing travel
              sequences with starting points...
            </Typography>
          </Box>
        )}
      </Paper>

      {/* ðŸ"¥ ENHANCED: Route Suggestions with starting point info */}
      {routeSuggestions.length > 0 && !createdRoutes.length && (
        <Paper elevation={0} sx={{ p: 3, mb: 3, border: "1px solid #E2E8F0", borderRadius: 3 }}>
          <Typography variant="h6" gutterBottom>
            ✨ Optimized Route Suggestions
          </Typography>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Based on medical professional assignments and travel optimization
            with starting points:
          </Typography>

          <Grid container spacing={3}>
            {routeSuggestions.map((route, index) => (
              <Grid key={index} size={{ xs: 12, md: 6 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                    >
                      <Typography variant="h6">{route.name}</Typography>
                      <Box display="flex" gap={1}>
                        <Chip
                          label={`${route.orders.length} orders`}
                          color="primary"
                          size="small"
                        />
                        {route.optimized && (
                          <Chip
                            icon={<OptimizedIcon />}
                            label="Optimized"
                            color="success"
                            size="small"
                          />
                        )}
                      </Box>
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      👨‍⚕️ {route.medical_professional_name}
                    </Typography>

                    {/* ðŸ"¥ NEW: Show starting point info */}
                    {route.starting_point && (
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <LocationIcon color="success" />
                        <Typography variant="caption" color="success.main">
                          Starts from:{" "}
                          {route.starting_point.name || "Custom location"}
                        </Typography>
                      </Box>
                    )}

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      ⱏ️ Est. Duration:{" "}
                      {route.estimated_duration_minutes ||
                        estimateRouteDuration(route.orders)}{" "}
                      minutes
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Total Distance: ~{route.total_distance_miles || "N/A"}{" "}
                      miles
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" gutterBottom>
                      Route stops:
                    </Typography>
                    <List dense>
                      {route.orders.slice(0, 3).map((order, orderIndex) => (
                        <ListItem key={orderIndex} sx={{ pl: 0 }}>
                          <ListItemAvatar>
                            <Avatar
                              sx={{
                                bgcolor: "primary.main",
                                width: 24,
                                height: 24,
                              }}
                            >
                              <Typography variant="caption">
                                {orderIndex + 1}
                              </Typography>
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={order.patient_name}
                            secondary={order.address}
                            sx={{ ml: 1 }}
                          />
                        </ListItem>
                      ))}
                      {route.orders.length > 3 && (
                        <ListItem sx={{ pl: 0 }}>
                          <ListItemText
                            secondary={`... and ${
                              route.orders.length - 3
                            } more stops`}
                            sx={{ ml: 4 }}
                          />
                        </ListItem>
                      )}
                    </List>

                    <Box mt={2}>
                      <Button
                        size="small"
                        startIcon={<MapIcon />}
                        onClick={() => openInGoogleMaps(route)}
                        variant="outlined"
                      >
                        Preview in Maps
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              color="success"
              onClick={handleCreateRoutes}
              disabled={creating}
              startIcon={<SaveIcon />}
            >
              {creating ? "Creating Routes..." : "✅ Create These Routes"}
            </Button>
          </Box>

          {creating && (
            <Box sx={{ mt: 3 }}>
              <LinearProgress />
              <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                Creating route groups and assigning orders...
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Created Routes */}
      {createdRoutes.length > 0 && (
        <Paper elevation={0} sx={{ p: 3, border: "1px solid #E2E8F0", borderRadius: 3 }}>
          <Typography variant="h6" gutterBottom>
            ✅ Successfully Created Routes
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Your optimized routes have been created and are ready for use.
          </Typography>

          <Grid container spacing={3}>
            {createdRoutes.map((route) => (
              <Grid key={route.id} size={{ xs: 12, md: 6 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6">{route.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      📅 Scheduled:{" "}
                      {new Date(
                        route.scheduled_date + "T00:00:00"
                      ).toLocaleDateString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      📊 Status: {route.status}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Orders: {route.order_count || "Loading..."}
                    </Typography>

                    <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => navigate(`/routes/${route.id}`)}
                      >
                        View Details
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => navigate(`/routes/${route.id}/assign`)}
                      >
                        👨‍⚕️ Assign MP
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate("/routes")}
            >
              View All Routes
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setRouteSuggestions([]);
                setCreatedRoutes([]);
                setSuccess("");
                loadExistingRoutes();
              }}
            >
              Optimize Again
            </Button>
          </Box>
        </Paper>
      )}

      {/* Settings Dialog */}
      <Dialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Smart Optimization Settings</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid  size={{ xs: 12, md: 6 }}>
              <Typography gutterBottom>Maximum Stops per Route</Typography>
              <Slider
                value={optimizationSettings.maxStopsPerRoute}
                onChange={(e, value) =>
                  setOptimizationSettings({
                    ...optimizationSettings,
                    maxStopsPerRoute: value,
                  })
                }
                min={3}
                max={12}
                step={1}
                marks
                valueLabelDisplay="on"
              />
            </Grid>

            <Grid  size={{ xs: 12, md: 6 }}>
              <Typography gutterBottom>
                Buffer Time Between Stops (minutes)
              </Typography>
              <Slider
                value={optimizationSettings.bufferTimeMinutes}
                onChange={(e, value) =>
                  setOptimizationSettings({
                    ...optimizationSettings,
                    bufferTimeMinutes: value,
                  })
                }
                min={5}
                max={30}
                step={5}
                marks
                valueLabelDisplay="on"
              />
            </Grid>

            <Grid  size={{ xs: 12 }}>
              <TextField
                label="Default Starting Point for MPs without configured starting points"
                fullWidth
                value={optimizationSettings.defaultStartingPoint}
                onChange={(e) =>
                  setOptimizationSettings({
                    ...optimizationSettings,
                    defaultStartingPoint: e.target.value,
                  })
                }
                helperText={
                  <Box component="span">
                    This address is used as fallback when a medical professional
                    doesn't have a starting point configured. Individual MP
                    starting points take priority.
                    {medicalProfessionals.length > 0 && (
                      <Box
                        component="div"
                        sx={{ mt: 1, fontSize: "0.875rem", color: "info.main" }}
                      >
                        {medicalProfessionals.find(
                          (mp) => mp.has_starting_point
                        ) ? (
                          <>
                            Auto-set from{" "}
                            {medicalProfessionals.find(
                              (mp) => mp.has_starting_point
                            )?.user?.full_name || "configured MP"}
                            's starting point
                          </>
                        ) : (
                          <>
                            No medical professionals have starting points
                            configured - using facility default
                          </>
                        )}
                      </Box>
                    )}
                  </Box>
                }
                placeholder="123 Medical Center Dr, Los Angeles, CA"
                InputProps={{
                  endAdornment: medicalProfessionals.find(
                    (mp) => mp.has_starting_point
                  ) ? (
                    <Tooltip title="Automatically set from medical professional's starting point">
                      <CheckCircleIcon color="success" />
                    </Tooltip>
                  ) : (
                    <Tooltip title="Using facility default - configure MP starting points for optimization">
                      <WarningIcon color="warning" />
                    </Tooltip>
                  ),
                }}
              />
            </Grid>

            <Grid  size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={optimizationSettings.prioritizeUrgent}
                    onChange={(e) =>
                      setOptimizationSettings({
                        ...optimizationSettings,
                        prioritizeUrgent: e.target.checked,
                      })
                    }
                  />
                }
                label="Prioritize Urgent Orders"
              />
            </Grid>

            <Grid  size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={optimizationSettings.useGoogleMapsOptimization}
                    onChange={(e) =>
                      setOptimizationSettings({
                        ...optimizationSettings,
                        useGoogleMapsOptimization: e.target.checked,
                      })
                    }
                  />
                }
                label="Use Google Maps Route Optimization"
              />
            </Grid>

            <Grid  size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={optimizationSettings.considerTrafficPatterns}
                    onChange={(e) =>
                      setOptimizationSettings({
                        ...optimizationSettings,
                        considerTrafficPatterns: e.target.checked,
                      })
                    }
                  />
                }
                label="Consider Traffic Patterns"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => setSettingsOpen(false)}>
            Save Settings
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reorder Dialog */}
      <ReorderDialog
        open={reorderDialogOpen}
        route={selectedRouteForReorder}
        onClose={() => {
          setReorderDialogOpen(false);
          setSelectedRouteForReorder(null);
        }}
        onSave={handleReorderSave}
      />
    </Box>
  );
};

// Reorder Dialog Component (unchanged)
const ReorderDialog = ({ open, route, onClose, onSave }) => {
  const [reorderedItems, setReorderedItems] = useState([]);

  useEffect(() => {
    if (route?.orders) {
      setReorderedItems([...route.orders]);
    }
  }, [route]);

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(reorderedItems);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setReorderedItems(items);
  };

  const handleSave = () => {
    onSave(reorderedItems);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Reorder Route Stops</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" paragraph>
          Drag and drop to reorder the stops in this route for optimal travel
          sequence.
        </Typography>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="reorder-list">
            {(provided) => (
              <List {...provided.droppableProps} ref={provided.innerRef}>
                {reorderedItems.map((order, index) => (
                  <Draggable
                    key={order.id}
                    draggableId={order.id.toString()}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <ListItem
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        sx={{
                          mb: 1,
                          border: "1px solid #ddd",
                          borderRadius: 1,
                          backgroundColor: snapshot.isDragging
                            ? "action.hover"
                            : "background.paper",
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: "primary.main" }}>
                            {index + 1}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={order.patient_name}
                          secondary={order.patient_address || order.address}
                        />
                        <DragIcon color="action" />
                      </ListItem>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </List>
            )}
          </Droppable>
        </DragDropContext>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          Save New Order
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RouteOptimization;
